"use client";

import { Badge } from "@ui/components/badge";
import { Card, CardContent } from "@ui/components/card";
import { Maximize2, X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";

interface ExampleItem {
	id: string;
	image: {
		src: string;
		alt: string;
	};
	description: string;
}

export default function ImageComparisonShowcase() {
	const t = useTranslations();
	const locale = useLocale();
	const [zoomedImage, setZoomedImage] = useState<string | null>(null);
	const exampleItems: ExampleItem[] = [
		{
			id: "gundam",
			image: {
				src: "/images/image-to-prompt/gundam.webp",
				alt: t(
					"imageToPrompt.components.imageComparison.generatedGundamAlt",
				),
			},
			description: t(
				"imageToPrompt.components.imageComparison.gundamDescription",
			),
		},
		{
			id: "futuristic-music-studio",
			image: {
				src: "/images/image-to-prompt/futuristic-female-synthesizer-player-in-neon-studio.webp",
				alt: t(
					"imageToPrompt.components.imageComparison.futuristicStudioAlt",
				),
			},
			description: t(
				"imageToPrompt.components.imageComparison.futuristicStudioDescription",
			),
		},
		{
			id: "bar-staircase",
			image: {
				src: "/images/image-to-prompt/blonde-woman-ascending-stone-stairs-dimly-lit-bar.webp",
				alt: t(
					"imageToPrompt.components.imageComparison.barStaircaseAlt",
				),
			},
			description: t(
				"imageToPrompt.components.imageComparison.barStaircaseDescription",
			),
		},
		{
			id: "night-cityscape-portrait",
			image: {
				src: "/images/image-to-prompt/red-eyed-woman-portrait-night-cityscape-bokeh.webp",
				alt: t(
					"imageToPrompt.components.imageComparison.nightCityscapeAlt",
				),
			},
			description: t(
				"imageToPrompt.components.imageComparison.nightCityscapeDescription",
			),
		},
	];

	const handleZoom = (imageSrc: string) => {
		setZoomedImage(imageSrc);
	};

	const closeZoom = () => {
		setZoomedImage(null);
	};

	return (
		<div className="w-full py-10">
			{/* Lightbox modal */}
			{zoomedImage && (
				<dialog
					className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-0 m-0 max-w-none max-h-none w-full h-full"
					onClick={closeZoom}
					onKeyDown={(e) => {
						if (e.key === "Escape") {
							closeZoom();
						}
					}}
					open
				>
					<button
						type="button"
						className="absolute top-4 right-4 bg-black/60 text-white p-2 rounded-full hover:bg-black/80 transition-colors"
						onClick={(e) => {
							e.stopPropagation();
							closeZoom();
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.stopPropagation();
								closeZoom();
							}
						}}
						aria-label={t("shared.actions.close")}
					>
						<X size={24} />
					</button>
					<div
						className="max-w-[90vw] max-h-[90vh]"
						onClick={(e) => e.stopPropagation()}
						onKeyDown={(e) => e.stopPropagation()}
					>
						<img
							src={zoomedImage}
							alt={t(
								"imageToPrompt.components.imageComparison.enlargedViewAlt",
							)}
							className="max-w-full max-h-[90vh] object-contain"
						/>
					</div>
				</dialog>
			)}

			<div className="text-center mb-8">
				<h2
					className="text-3xl font-bold text-center mb-10 bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent"
					key={`comparison-title-${locale}`}
				>
					{t("imageToPrompt.components.imageComparison.title")}
				</h2>

				<p className="text-slate-600 max-w-3xl mx-auto">
					{t("imageToPrompt.components.imageComparison.description")}
				</p>
			</div>

			<div className="relative">
				<div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
					{exampleItems.map((item) => (
						<div
							key={item.id}
							className="bg-slate-50 rounded-xl p-6 shadow-inner hover:shadow-md transition-shadow duration-300"
						>
							<div className="grid md:grid-cols-2 gap-6 items-center">
								{/* Left side - Generated image only */}
								<div className="relative group">
									<Card className="overflow-hidden border-0 shadow-md h-full">
										<CardContent className="p-0 aspect-square relative">
											<img
												src={
													item.image.src ||
													"/placeholder.svg"
												}
												alt={item.image.alt}
												className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
											/>
											<button
												type="button"
												className="absolute bottom-2 right-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-black/80 transition-colors"
												aria-label={t(
													"imageToPrompt.components.imageComparison.zoomImage",
												)}
												onClick={(e) => {
													e.stopPropagation();
													handleZoom(item.image.src);
												}}
												onKeyDown={(e) => {
													if (
														e.key === "Enter" ||
														e.key === " "
													) {
														e.stopPropagation();
														handleZoom(
															item.image.src,
														);
													}
												}}
											>
												<Maximize2 size={16} />
											</button>
										</CardContent>
									</Card>
								</div>

								{/* Right side - Description text */}
								<div className="flex items-center h-full">
									<p className="text-slate-700 text-base leading-relaxed">
										{item.description}
									</p>
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	);
}
